import React from "react";
import { Plus, Minus, X } from "lucide-react";
import { formatMoney, itName, itLineTotal } from "../../../lib/retailFormat";
import { lineSpiceFor as spiceOf } from "../../../lib/retailFormat";
import TakeoutBadge from "../TakeoutBadge";

function Row({ label, value, muted }) {
  return (
    <div className={"flex items-center justify-between " + (muted ? "text-emerald-200/70" : "text-emerald-100")}>
      <span className="text-xs uppercase tracking-wider">{label}</span>
      <span className="text-sm">{value}</span>
    </div>
  );
}

// Phase 3M — extracted verbatim from RetailCustomer.jsx::MobileCartButton.
// Sticky floating cart button visible only on <lg and when cartCount > 0.
export function MobileCartButton({ cartCount, cart, currency, onClick }) {
  if (cartCount === 0) return null;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="retail-cart-cta"
      className="lg:hidden fixed left-1/2 -translate-x-1/2 bottom-5 z-30 px-5 py-3 rounded-full bg-orange-500 text-white font-semibold shadow-2xl shadow-orange-700/50 hover:bg-orange-600 active:scale-95 inline-flex items-center gap-3"
    >
      <span className="inline-flex items-center gap-2">
        <span className="bg-white text-orange-600 rounded-full w-6 h-6 grid place-items-center text-xs font-bold" data-testid="retail-cart-cta-count">{cartCount}</span>
        View Cart
      </span>
      <span className="text-sm font-bold">{formatMoney(cart?.total ?? cart?.subtotal ?? 0, currency)}</span>
    </button>
  );
}

// Phase 3M — extracted verbatim from RetailCustomer.jsx::MobileCartDrawer.
// Slide-in right drawer that mirrors the desktop CartPanel.
export default function MobileCartDrawer({
  cart, currency, onClose, onSetQty, onRemove, onCheckout,
  // Item-level Takeout marker (Sep 2026) — mirrors CartPanel exactly, so
  // the phone drawer and the desktop panel offer the same thing. Both
  // optional: omit them and this renders as it did before.
  orderType, onSetItemTakeout,
}) {
  const items = cart?.items || [];
  const takeoutOfferable = Boolean(onSetItemTakeout)
    && (orderType === "dine_in" || orderType === "walk_in");
  const subtotal = Number(cart?.subtotal ?? 0);
  // Product tax treatment (Oct 2026) — tax and total are the SERVER's:
  // the cart is priced by the same engine as checkout, so tax-exempt and
  // custom-rate products are already reflected. A cart without figures
  // shows "calculated at checkout" — never an invented flat-rate amount.
  const hasServerTotals = cart?.tax != null && cart?.total != null;
  const taxLabel = hasServerTotals ? formatMoney(Number(cart.tax), currency) : "Calculated at checkout";
  const totalLabel = hasServerTotals ? formatMoney(Number(cart.total), currency) : formatMoney(subtotal, currency);
  return (
    <div className="fixed inset-0 z-40 lg:hidden" data-testid="retail-cart-drawer">
      <div className="absolute inset-0 bg-black/60" onClick={onClose}/>
      <div className="absolute right-0 top-0 bottom-0 w-full sm:w-[420px] bg-[#0a1a0e] text-emerald-50 flex flex-col">
        <div className="px-4 py-3 border-b border-emerald-900/40 flex items-center gap-3">
          <button onClick={onClose} aria-label="Close cart"><X className="w-5 h-5"/></button>
          <div className="font-serif text-lg flex-1">Your Order</div>
          {items.length > 0 && (
            <span className="text-[11px] bg-orange-500 px-2 py-0.5 rounded-full" data-testid="retail-cart-drawer-count">{items.reduce((s,i)=>s+(i.quantity||0),0)}</span>
          )}
        </div>
        <div className="flex-1 overflow-y-auto p-3 space-y-2">
          {items.length === 0 ? (
            <div className="text-center text-sm italic text-emerald-200/60 py-12">Your cart is empty.</div>
          ) : items.map((it) => (
            <div key={it.item_id || it.id} className="bg-[#102718] rounded-md p-3 text-sm border border-emerald-900/40">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="font-medium">{itName(it)}</div>
                  {spiceOf(it) && (
                    <div className="text-[11px] text-amber-200/90 mt-0.5" data-testid={`retail-drawer-spice-${it.item_id || it.id}`}>
                      🌶 {spiceOf(it).short} · {spiceOf(it).label}
                    </div>
                  )}
                  {it.item_takeout && (
                    <div className="mt-1"><TakeoutBadge dark testId={`retail-drawer-item-takeout-badge-${it.item_id || it.id}`} /></div>
                  )}
                </div>
                <div className="text-orange-300 font-semibold">{formatMoney(itLineTotal(it), currency)}</div>
              </div>
              <div className="mt-2 flex items-center gap-2">
                <button onClick={() => {
                  const next = (it.quantity || 0) - 1;
                  if (next <= 0) onRemove(it.item_id || it.id);
                  else onSetQty(it.item_id || it.id, next);
                }} className="w-7 h-7 rounded bg-orange-500 grid place-items-center"><Minus className="w-3.5 h-3.5"/></button>
                <span className="min-w-[1.5ch] text-center">{it.quantity}</span>
                <button onClick={() => onSetQty(it.item_id || it.id, (it.quantity||0)+1)} className="w-7 h-7 rounded bg-orange-500 grid place-items-center"><Plus className="w-3.5 h-3.5"/></button>
                {takeoutOfferable && (
                  <button
                    type="button"
                    onClick={() => onSetItemTakeout(it.item_id || it.id, !it.item_takeout)}
                    aria-pressed={!!it.item_takeout}
                    data-testid={`retail-drawer-item-takeout-${it.item_id || it.id}`}
                    title="Pack this item to take away. The rest of the order stays Dine-In."
                    className={
                      "ml-auto h-9 px-2 rounded text-[10px] font-bold uppercase tracking-wider border "
                      + (it.item_takeout
                        ? "bg-amber-400/20 text-amber-200 border-amber-300/50"
                        : "bg-transparent text-emerald-200/70 border-emerald-200/25")
                    }
                  >
                    {it.item_takeout ? "Takeout ✓" : "Takeout"}
                  </button>
                )}
                <button
                  onClick={() => onRemove(it.item_id || it.id)}
                  className={(takeoutOfferable ? "" : "ml-auto ") + "text-xs text-emerald-200/60 hover:text-orange-300"}
                >Remove</button>
              </div>
            </div>
          ))}
        </div>
        <div className="px-4 py-3 border-t border-emerald-900/40 text-sm">
          <Row label="Subtotal" value={formatMoney(subtotal, currency)} />
          <Row label="Tax" value={taxLabel} muted />
          <div className="flex items-center justify-between mt-2 pt-2 border-t border-emerald-900/50">
            <span className="font-bold">TOTAL</span>
            <span className="font-extrabold text-xl text-orange-300">{totalLabel}</span>
          </div>
          <button
            type="button"
            onClick={onCheckout}
            disabled={items.length === 0}
            data-testid="retail-mobile-checkout-btn"
            className={"mt-3 w-full h-11 rounded-md font-semibold text-white " + (items.length === 0 ? "bg-orange-500/40 cursor-not-allowed" : "bg-orange-500 hover:bg-orange-600")}
          >
            Proceed to Checkout →
          </button>
        </div>
      </div>
    </div>
  );
}
